import { FC, useCallback, useEffect, useState } from "react";
import { setModalState, useModalStore } from "../..";
import ModalTitle from "../../components/ModalTitle";
import {
  SortType,
  LoanType,
  BorrowingStats,
  FeeObject,
  FeeRateOptions,
} from "../../../types";
import useMyInscriptions from "../../../hooks/api/useMyInscriptions";
import Content from "../../../components/Reusable/Content";
import StepButtons from "../../components/StepButtons";
import StatsSection from "../../../components/Reusable/StatsSection/StatsSection";
import { calculateRepaymentInfo, convertSatToBtc } from "../../../utils";
import useDeepLake from "../../../hooks/deeplake/useDeepLake";
import ShareModal from "../ShareModal";
import useBtcPrice from "../../../hooks/api/useBtcPrice";
import useAuth from "../../../auth";
import SelectWalletModal from "../SelectWalletModal/SelectWalletModal";
import useFees from "../../../hooks/api/useFees";
import FeeSelector from "../../../components/Reusable/FeeSelector";
import InformationBar from "../../components/InformationBar";
import useMyTransfers from "../../../hooks/api/useMyValidTransfers";
import Loading from "../../../components/Reusable/Loading";
import InscribeButton from "../../../components/InscribeButton";

interface BorrowModalProps {
  request: LoanType;
  stats?: BorrowingStats | undefined;
  onSubmit?: Function;
  isBRC20?: boolean;
}

const BorrowModal: FC<BorrowModalProps> = ({
  request,
  stats,
  onSubmit,
  isBRC20,
}) => {
  const [selectedBRC20Id, setSelectedBRC20Id] = useState("");
  const tokenAmount = request?.bundleSize;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedInscriptions, setSelectedInscriptions] = useState<any[]>([]);
  const [repaymentAmount, setRepaymentAmount] = useState<number>(0);
  const [updatedStats, setUpdatedStats] = useState<
    BorrowingStats | undefined
  >();
  const [sortType, setSortType] = useState<SortType | null>(
    SortType.ME_Price_ASC,
  );
  const [selectedFeeRate, setSelectedFeeRate] = useState<FeeObject>({
    label: FeeRateOptions.Medium,
    value: 0,
  } as FeeObject);
  const { fees, loading, error: feeError } = useFees();
  const { btcPrice, isLoading } = useBtcPrice();
  const { onConnectClick, isUserSignedIn } = useAuth();

  const { close } = useModalStore();
  const { myInscriptions, isFetching, error } = useMyInscriptions({
    collectionSymbol: request.collectionSymbol ?? undefined,
    sortBy: sortType,
  });
  const [needsInscription, setNeedsInscription] = useState<boolean>(false);
  const [needsMoreTokens, setNeedsMoreTokens] = useState<boolean>(false);

  const {
    isLoading: isLoadingTransfers,
    error: errorTransfers,
    myTransfers,
    myAvailableBalance,
    fetchMyTransfers,
  } = useMyTransfers({
    ticker: request.ticker ?? undefined,
  });
  useEffect(() => {
    if (isBRC20) {
      fetchMyTransfers();
    }
  }, [isBRC20]);

  useEffect(() => {
    if (
      isBRC20 &&
      myAvailableBalance !== undefined &&
      request?.bundleSize &&
      myTransfers?.length === 0 &&
      !isLoadingTransfers &&
      !errorTransfers
    ) {
      if (myAvailableBalance >= request?.bundleSize) setNeedsInscription(true);
      else if (myAvailableBalance < request?.bundleSize) {
        setNeedsInscription(false);
        setNeedsMoreTokens(true);
      }
    } else {
      setNeedsInscription(false);
      setNeedsMoreTokens(false);
    }
  }, [isBRC20, myAvailableBalance, myTransfers, isLoadingTransfers, errorTransfers]);


  // State variable to control the visibility of your JSX
  const [showRepayInfo, setShowRepayInfo] = useState(false);

  // Effect hook to update showContent based on loading states and array lengths
  useEffect(() => {
    if ((!isLoadingTransfers && myTransfers.length > 0) || 
        (!isFetching && myInscriptions.length > 0)) {
      setShowRepayInfo(true);
    } else {
      setShowRepayInfo(false);
    }
  }, [isLoadingTransfers, isFetching, myTransfers, myInscriptions]);

  const handleSelect = useCallback(
    (ins: any) => {
      setSelectedInscriptions((prev: any[]) => {
        if (prev.length > 0 && prev[0].id === ins.id) return [];
        else {
          return [ins];
        }
      });
    },
    [setSelectedInscriptions],
  );

  const handleConnect = useCallback(async () => {
    setModalState(
      "Select wallet modal",
      () => {},
      () => <SelectWalletModal />,
      false,
      false,
    );
  }, [setModalState]);

  const { handleAcceptOffer } = useDeepLake();
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    const updatedLoan: any = await handleAcceptOffer(
      selectedInscriptions,
      request,
      fees[selectedFeeRate.label ?? "medium"],
      async () => {
        setIsSubmitting(false);
        setModalState(
          "Share",
          () => {},
          () => <ShareModal type="borrow" isBRC20={isBRC20} />,
          false,
          false,
        );
        await onSubmit?.();
      },
    );
    // TODO: need to check if updatedLoan is undefined, if so, show error message
    // if (!updatedLoan) return;
  }, [
    handleAcceptOffer,
    request,
    onSubmit,
    selectedInscriptions,
    selectedFeeRate,
    fees,
  ]);

  //Remove LTV stat from stats array
  useEffect(() => {
    if (stats) {
      const filterStats = ({
        LTV,
        ...rest
      }: BorrowingStats): Omit<BorrowingStats, "LTV"> => rest;
      const updatedStats = filterStats(stats);

      // Update the name of bestOffer to "Loan Amount"
      if (updatedStats?.bestOffer) {
        updatedStats.bestOffer.name = "Loan Amount";
        setUpdatedStats({ ...updatedStats });
      }
      setUpdatedStats({ ...updatedStats });
    }
  }, [stats]);

  // Calculate repayment amount
  useEffect(() => {
    const { totalRepayment } = calculateRepaymentInfo(
      +request.principal,
      +request.interestRate,
      +request.term,
    );
    setRepaymentAmount(totalRepayment);
  }, [request]);

  const btcValue = parseFloat(
    convertSatToBtc(
      false,
      parseInt(request?.collection?.totalVolume?.toString() || "0"),
    ).toString(),
  ).toFixed(2);
  const subtitle = isBRC20
    ? `Market Cap: ${btcValue} BTC`
    : `Total Volume: ${btcValue} BTC`;

  return (
    <div className="sm:w-[85vw] w-[100vw] max-w-[660px] bg-gray-800 sm:p-7 p-5 rounded-2xl flex flex-col sm:mx-3 mx-0 border border-white/20">
      {request.collection && (
        <ModalTitle
          collectionSymbol={request.collectionSymbol}
          img={request.collection.imageURI}
          title={request.collection.name}
          subtitle={subtitle}
          isBRC20={isBRC20}
        />
      )}
      <div className="mt-5">
        <StatsSection
          statItems={updatedStats}
          size="small"
          cardMinWidth={120}
        />
      </div>
      <p className="text-2xl font-bold text-gray-50 mt-8">Collateral</p>
      <div className="mt-2">
        {isBRC20 ? (
          <>
            {isLoadingTransfers ? (
              <Loading />
            ) : myTransfers?.length !== 0 ? (
              myTransfers?.map((transfer: any) => (
                <div
                  className={`border flex gap-4 items-center rounded-md p-3 bg-zinc-900 hover:bg-black/10 hover:border-white/40 cursor-pointer transition-all mt-3 ${
                    selectedBRC20Id === transfer.id
                      ? "border-white ring-1 ring-white hover:border-white/80"
                      : "border-white/20"
                  }`}
                  onClick={() => {
                    setSelectedBRC20Id(transfer.id);
                    handleSelect(transfer);
                  }}
                >
                  <div
                    className={`w-4 h-4 rounded-full border  ml-2 ${
                      selectedBRC20Id === transfer.id
                        ? " bg-white border-white"
                        : "border-white/40"
                    } `}
                  ></div>
                  <div
                    className={`flex items-center gap-3  transition-opacity ${
                      selectedBRC20Id === transfer.id ? "opacity-100" : "opacity-80"
                    }`}
                  >
                    <img
                      src={request.collection?.imageURI}
                      className="h-12 w-12 rounded-md"
                    />
                    <p className="text-zinc-100 font-bold sm:text-base text-sm ">{`${tokenAmount} x ${request?.ticker}`}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-zinc-300 font-semibold text-center text-sm sm:text-base mt-2">{`No valid ${request?.ticker} transfers for ${request?.bundleSize} tokens.`}</p>
            )}
          </>
        ) : (
          <Content
            contentName={`"${request?.collection?.name}" Available`}
            isLoading={isFetching}
            visibleItems={myInscriptions}
            onClick={handleSelect}
            variant="Wallet"
            selectedItems={selectedInscriptions}
            error={error}
          />
        )}
      </div>
      <div className="mt-10">
        {showRepayInfo && (
          <div className="text-xs sm:text-sm text-gray-50 font-semibold flex items-center justify-center bg-gray-600 border border-white/20 rounded-xl p-3 w-full">
            <p className="">
              You have to repay a total of{" "}
              <strong>
                {" "}
                ₿{convertSatToBtc(false, repaymentAmount)} |{" "}
                {btcPrice && (
                  <>
                    {parseFloat(
                      convertSatToBtc(false, repaymentAmount).toString(),
                    ) > 0
                      ? "$" +
                        (
                          parseFloat(
                            convertSatToBtc(false, repaymentAmount).toString(),
                          ) * btcPrice
                        ).toFixed(2)
                      : ""}{" "}
                  </>
                )}
              </strong>{" "}
              within <strong>{request.term} Days.</strong>
            </p>
          </div>
        )}
        {!isLoadingTransfers && (
          <div className="mt-4">
            {needsInscription ? (
              <InscribeButton request={request} />
            ) : needsMoreTokens ? (
              <div className="mb-7 mt-7">
                <InformationBar
                  text={`You need a transfer of exactly ${tokenAmount} ${request?.ticker} to borrow, but you only have ${myAvailableBalance} ${request?.ticker} available.`}
                  gray
                />
              </div>
            ) : showRepayInfo ? (
              <FeeSelector
                selectedFeeRate={selectedFeeRate}
                setSelectedFeeRate={setSelectedFeeRate}
              />
            ): (<></>)}
          </div>
        )}
        <div className="mt-8">
          <StepButtons
            leftText="CANCEL"
            rightText={isUserSignedIn ? "BORROW" : "CONNECT"}
            leftOnClick={() => close()}
            rightOnClick={isUserSignedIn ? handleSubmit : handleConnect}
            rightSubtitle={
              isUserSignedIn
                ? convertSatToBtc(false, request.principal) + " BTC"
                : ""
            }
            isSubmitting={isSubmitting}
            disabled={
              isUserSignedIn
                ? isBRC20
                  ? selectedBRC20Id === "" || loading || !!feeError || needsInscription
                  : selectedInscriptions.length === 0 || loading || !!feeError
                : false
            }
          />
        </div>
      </div>
    </div>
  );
};

export default BorrowModal;
